import { useEffect, useContext, useCallback } from 'react';
import { AssessmentContext } from '../../context/AssessmentContext.js';
import { WebSocketContext } from '../../context/WebSocketContext.js';
import useSessionStorage from '../../hooks/useSessionStorage.js';
import { LearnosityContainerObservable } from './LearnosityContainer.js';
import { PWAEventsObservable } from './WebsocketMessageHandler.js';
import {
  LEARNOSITY_EVENTS,
  showCustomDialog,
  hideCustomDialog
} from '../../scripts/learnosityScripts.js';
import {
  MIN_CONSECUTIVE_ERRORS,
  PAUSED_BY_PROCTOR,
  PAUSED_BY_SYSTEM,
  PAUSED_BY_TESTER,
  SAVE_ERROR,
  SAVE_SUCCESS,
  TEST_INSESSION,
  TEST_PAUSED,
  TEST_LAUNCHED,
} from './TestStatus.js';
import * as PwaEvents from '../../context/PwaEvents.js';
import * as Storage from '../common/Storage.js';
import { sleep } from '../../scripts/common/helper.js';
import {
  PROCTOR_PAUSE_DIALOG,
  TESTER_SAVE_DIALOG,
} from './LearnosityDialogs.js';
import { KioskContext } from '../../context/KioskProviderContext.js';
import { WINDOWS } from '../../scripts/Platform.js';
import {
  hasNetworkErrors,
  NETWORK_ERROR_ACTION,
} from '../../scripts/network-error/networkError.js';

const TestEventHandler = () => {
  const { isSecure, platform } = useContext(KioskContext);
  const { clearStorage, getStorageItem, removeStorageItem, setStorageItem } =
    useSessionStorage();
  const {
    testStatus,
    updateLaunchError,
    applySaveStateChanges,
    updateTestStatus,
    learnosityItemApp,
    setExit,
    setOpenErrorDialog,
    setSaveErrorCount,
    setOpenProctorPauseDialog,
    setOpenContinueDialog,
    setProctorPauseDialogProps,
    setOpenExitingDialog,
    setExitingDialogDetails,
    setOpenSubmitDialog,
    updateSubmitAttempts,
    submitAttempts,
    setSubmitInProgress,
  } = useContext(AssessmentContext);
  const { sendMsgToWebsocket } = useContext(WebSocketContext);


  const testerSaveAndExitSuccess = useCallback(
    (appInstance) => {
      setOpenErrorDialog(false);
      setStorageItem(
        Storage.STORAGE_PAUSED_REASON,
        PwaEvents.ACTOR_TESTER_ENDED
      );
      sendMsgToWebsocket(LEARNOSITY_EVENTS.TEST_FINISHED_SAVE);
      removeStorageItem(Storage.STORAGE_AUTH_TOKEN); // do not clear all yet, there's still pause event being handled. clearing will be done on exit(when dialog is closed)

      const dialog = TESTER_SAVE_DIALOG();
      setExitingDialogDetails(dialog);
      setOpenExitingDialog(true);
    },
    [
      setStorageItem,
      removeStorageItem,
      setOpenErrorDialog,
      sendMsgToWebsocket,
      setOpenExitingDialog,
      setExitingDialogDetails,
    ]
  );

  const testerSaveAndExitError = useCallback(() => {
    //note: need to set both testStatus & saveErrorCount, so the error dialog shows up immediately(invoked from AssessmentOverlay)
    applySaveStateChanges(SAVE_ERROR);
    setSaveErrorCount(MIN_CONSECUTIVE_ERRORS);
  }, [applySaveStateChanges, setSaveErrorCount]);

  const eventHandler = useCallback(
    async ({ appInstance, eventName, error, ready }) => {
      let pausedBy;
      switch (eventName) {
        case LEARNOSITY_EVENTS.TEST_SUBMIT_SUCCESS:
          //We do not want to exit out of waypoint on test submit success
          //the test:finished:submit will take care of exit
          removeStorageItem(Storage.STORAGE_AUTH_TOKEN); // remove auth_token will definitely prevent them from continuing. do not clear all yet, there's still other events being handled, clearing will be done on exit.
          sendMsgToWebsocket(eventName);
          const activityConfig = appInstance?.getActivity();
          // 1. Check if the outro page feature is enabled (defaults to true when show_outro is undefined)
          const showOutro = activityConfig?.config?.navigation?.show_outro;
          if (showOutro || showOutro === undefined) {
            //This sleep is to make sure any additional dialogs from learnosity is automatically handled
            await sleep(1000);
            hideCustomDialog(appInstance, () => {
              setSubmitInProgress(false);
              setOpenSubmitDialog(false);
            });
          }
          break;
        case PwaEvents.PROCTOR_SAVEANDEXIT:
          sendMsgToWebsocket(LEARNOSITY_EVENTS.TEST_PROCTOR_SAVE);
          sendMsgToWebsocket(LEARNOSITY_EVENTS.TEST_FINISHED_SAVE);
          removeStorageItem(Storage.STORAGE_AUTH_TOKEN); // do not clear all yet, there's still pause event being handled. clearing will be done on exit(when dialog is closed)
          break;
        case PwaEvents.PROCTOR_SUBMITANDEXIT:
          sendMsgToWebsocket(LEARNOSITY_EVENTS.TEST_PROCTOR_EXIT_AND_SUBMIT);
          sendMsgToWebsocket(LEARNOSITY_EVENTS.TEST_FINISHED_SUBMIT);
          removeStorageItem(Storage.STORAGE_AUTH_TOKEN); // do not clear all yet, there's still pause event being handled. clearing will be done on exit(when dialog is closed)
          break;
        case PwaEvents.PROCTOR_TERMINATE:
          sendMsgToWebsocket(LEARNOSITY_EVENTS.TEST_PROCTOR_TERMINATED);
          sendMsgToWebsocket(LEARNOSITY_EVENTS.TEST_FINISHED_DISCARD);
          removeStorageItem(Storage.STORAGE_AUTH_TOKEN); // do not clear all yet, there's still pause event being handled. clearing will be done on exit(when dialog is closed)
          break;
        case PwaEvents.TESTER_SAVEANDEXIT: //This event is generated by NMC custom Save/Exit button
          testerSaveAndExitSuccess(appInstance);
          break;
        case PwaEvents.TESTER_SAVEANDEXIT_ERROR: //This event is generated by NMC custom Save/Exit button on save error
          testerSaveAndExitError();
          break;
        case LEARNOSITY_EVENTS.TEST_FINISHED_SAVE: //This event is generated by Learnosity Save/Exit button
        case LEARNOSITY_EVENTS.TEST_FINISHED_SUBMIT:
        case LEARNOSITY_EVENTS.TEST_FINISHED_DISCARD:
          sendMsgToWebsocket(eventName);
          await sleep(1000);
          setExit(true);
          break;
        case LEARNOSITY_EVENTS.TEST_PROCTOR_TERMINATED:
          sendMsgToWebsocket(eventName);
          clearStorage();
          break;
        case LEARNOSITY_EVENTS.TEST_PROCTOR_SAVE:
          appInstance.save();
          sendMsgToWebsocket(eventName);
          clearStorage();
          break;
        case LEARNOSITY_EVENTS.TEST_PROCTOR_EXIT_AND_SUBMIT:
          appInstance.submit();
          sendMsgToWebsocket(eventName);
          clearStorage();
          break;
        case LEARNOSITY_EVENTS.TEST_START:
          //we cannot always set to inSession, 1 case: paused state then refresh
          if(testStatus === TEST_LAUNCHED){
            updateTestStatus(TEST_INSESSION)
          }
          break;
        case LEARNOSITY_EVENTS.TEST_READY:
          removeStorageItem(Storage.STORAGE_LAUNCH_AUTO_EXIT_SECONDS);
          sendMsgToWebsocket(PwaEvents.LEARNOSITY_LAUNCH_SUCCESS);
          //this takes care of refresh(pause on intro page only) - similar to what we do in ITEM_LOAD(pause when in session on refresh) minus the showCustomDialog
          //prohibited action type pause is handled in LessSecure component. the code below is for proctor pause only
          pausedBy = getStorageItem(Storage.STORAGE_PAUSED_BY);
          let statusBeforePause = getStorageItem(
            Storage.STORAGE_STATUS_BEFORE_PAUSE,
          );
          if (
            statusBeforePause === TEST_LAUNCHED &&
            pausedBy === PAUSED_BY_PROCTOR
          ) {
            setProctorPauseDialogProps(PROCTOR_PAUSE_DIALOG());
            setOpenProctorPauseDialog(true);
          }
          break;
        case LEARNOSITY_EVENTS.TEST_SAVE_ERROR:
          //Set submit In progress to undefined to make sure
          //Learnosity error save dialog does not show up in case the
          //user already submitted
          setSubmitInProgress();
          applySaveStateChanges(SAVE_ERROR, true);
          break;
        case LEARNOSITY_EVENTS.TEST_SAVE_SUCCESS:
          applySaveStateChanges(SAVE_SUCCESS);
          break;
        case LEARNOSITY_EVENTS.TEST_SUBMIT:
          updateSubmitAttempts();
          break;
        case LEARNOSITY_EVENTS.TEST_SUBMIT_PROGRESS:
          setSubmitInProgress(true);
          setOpenSubmitDialog(true);
          setStorageItem(
            Storage.STORAGE_PAUSED_REASON,
            PwaEvents.ACTOR_TESTER_ENDED,
          );
          break;
        case LEARNOSITY_EVENTS.TEST_ACTIVITYJSON:
        case LEARNOSITY_EVENTS.TEST_SUBMIT_ERROR:
          if (
            submitAttempts === 0 &&
            eventName === LEARNOSITY_EVENTS.TEST_ACTIVITYJSON
          ) {
            break;
          }
          setSubmitInProgress(false);
          setOpenSubmitDialog(true);
          applySaveStateChanges(SAVE_ERROR, true);
          const submissionData = appInstance.assessApp().getSubmissionData();
          sendMsgToWebsocket(
            'learnosity:submit:fail',
            true,
            null,
            submissionData && { submissionData },
          );
          break;
        case LEARNOSITY_EVENTS.TEST_PAUSE:
          updateTestStatus(TEST_PAUSED);
          break;
        case LEARNOSITY_EVENTS.ITEM_LOAD:
          pausedBy = getStorageItem(Storage.STORAGE_PAUSED_BY); //this is more reliable than state, this removes dependency on state pausedBy that will stop resubscribe during state change
          let __pausedReason = getStorageItem(Storage.STORAGE_PAUSED_REASON);
          let __hasNetworkErrors = hasNetworkErrors();
          if (testStatus === TEST_PAUSED && pausedBy === PAUSED_BY_PROCTOR) {
            showCustomDialog(appInstance, PROCTOR_PAUSE_DIALOG());
            setProctorPauseDialogProps(PROCTOR_PAUSE_DIALOG());
            setOpenProctorPauseDialog(true);
          } else if (
            testStatus === TEST_PAUSED &&
            pausedBy === PAUSED_BY_TESTER
          ) {
            appInstance?.dialogs().pause.show();
          } else if (
            testStatus === TEST_PAUSED &&
            pausedBy === PAUSED_BY_SYSTEM &&
            __pausedReason?.startsWith(NETWORK_ERROR_ACTION.PREFIX) &&
            !__hasNetworkErrors
          ) {            
            setOpenContinueDialog(true);
          } else if (
            testStatus === TEST_PAUSED &&
            pausedBy === PAUSED_BY_SYSTEM &&
            __pausedReason?.startsWith(NETWORK_ERROR_ACTION.PREFIX) &&
            __hasNetworkErrors
          ) {
            setOpenErrorDialog(true);
          }
          break;
        case PwaEvents.LEARNOSITY_LAUNCH_FAIL:
          //send learnosity fail to launch only if not ready
          //from learnosity, no save or websocket errors.
          const __attemptNumber =
            getStorageItem(Storage.STORAGE_ATTEMPT_NO) || 0;
          const __saveErrorCount =
            getStorageItem(Storage.STORAGE_SAVE_ERROR_COUNT) || 0;
          if (!ready && __saveErrorCount === 0 && __attemptNumber === 0) {
            updateLaunchError(true);
            sendMsgToWebsocket('learnosity:launch:fail', true, error);
          }
          break;
        default:
          break;
      }
    },
    [
      applySaveStateChanges,
      updateLaunchError,
      sendMsgToWebsocket,
      updateTestStatus,
      testStatus,
      getStorageItem,
      clearStorage,
      setExit,
      removeStorageItem,
      testerSaveAndExitSuccess,
      testerSaveAndExitError,
      setOpenProctorPauseDialog,
      setProctorPauseDialogProps,
      setOpenContinueDialog,
      setOpenErrorDialog,
      setOpenSubmitDialog,
      updateSubmitAttempts,
      submitAttempts,
      setSubmitInProgress,
      setStorageItem,
    ]
  );

  //UseEffect required. Subscribe and unsubscribe here
  //to make sure the event handler is registered and unregistered
  useEffect(() => {
    //priority on subscribe will call the methods in priority order
    //otherwise it will get invoked in the order of subscription
    LearnosityContainerObservable.subscribe(
      eventHandler,
      'testEventHandler',
      2
    );

    PWAEventsObservable.subscribe(eventHandler, 'testEventHandler', 2);
    return () => {
      // console.log('unmount test event handler');
      LearnosityContainerObservable.unsubscribe('testEventHandler');
      PWAEventsObservable.unsubscribe('testEventHandler');
    };
  }, [eventHandler]);

  //LearnosityContainerObservable.subscribe(eventHandler);
  //No need to unsubscribe since the component unloads when learnosity container unloads

  useEffect(() => {
    if (!learnosityItemApp) {
      return;
    }
    while (testStatus === TEST_PAUSED) {
      let intervalId = setInterval(() => {
        learnosityItemApp?.save();
      }, 30000);
      return () => {
        clearInterval(intervalId);
        intervalId = null;
      };
    }
  }, [testStatus, learnosityItemApp]);

  const tryToSaveAssessment = useCallback(
    (event) => {
      if (event.ctrlKey && event.altKey) {
        // In case of ctrl and alt is pressed to, lets make sure
        // we save the test
        learnosityItemApp?.save();
      }
    },
      [learnosityItemApp]
  );

  useEffect(() => {
    if (isSecure && platform === WINDOWS) {
      window.addEventListener('keydown', tryToSaveAssessment);
    }
    //This is for cleaning up when mounting. Prevent multiple event handlers.
    return () => {
      window.removeEventListener('keydown', tryToSaveAssessment);
    };
  }, [isSecure, platform, tryToSaveAssessment]);

  return <></>;
};
export default TestEventHandler;
